import { mapGetters } from 'vuex';
import Themable from '@/common/mixins/themable.mixin';
import LeaveGuard from '@/common/mixins/leaveguard.mixin';
import { validationMixin } from 'vuelidate';
import EventListener from '@/common/mixins/eventlistener.mixin';
import { EventService } from '@/services/event.service';
import ClientMixin from '@/common/mixins/unauthenticated.client.mixin';
import ProgressPollingMixin from '@/common/mixins/progress-polling.mixin';
import { PURGE_AUTHENTICATED_INTERVIEW } from '@/stores/actions.type';
import {
  UPDATE_INTERVIEW,
  GET_INTERVIEW_PROGRESS,
} from '@/stores/actions.type';
import {
  NOTIFICATION_TYPES,
  NotificationService,
} from '@/services/notification.service';
import { MENUS } from '@/common/constants';

const InterviewClientMixin = {
  data: () => ({
    ignoreInProgessClient: false,
    formHidden: false,
  }),

  mixins: [validationMixin, EventListener, LeaveGuard, Themable, ClientMixin, ProgressPollingMixin],

  computed: {
    ...mapGetters([
      'clientProgress',
      'clientData',
      'isAuthenticated',
      'isClientAuthenticated',
    ]),
  },

  mounted() {
    // Start polling for progress changes to update sync status
    this.startPolling();
  },

  beforeDestroy() {
    // Clean up polling when component is destroyed
    this.stopPolling();
  },

  methods: {
    // Toggle Help Drawer
    toggleHelp() {
      EventService.emit('toggle-help');
    },

    /* Always ask to save when leaving page */
    showSaveFunction() {
      return true;
    },

    /*
      Called before we exit the page
      If we are leaving the interview process, delete the working interview
    */
    beforeExit(route) {
      // Check if route has a path (it might be a route object with just name)
      let routePath = route.path;
      
      // If no path, try to resolve it from the route name
      if (!routePath && route.name) {
        // Don't purge if navigating to another client interview page
        const clientInterviewPages = [
          MENUS.CLIENT.CITIZENSHIP.id,
          MENUS.CLIENT.LEGAL_NAME.id,
          MENUS.CLIENT.LEGAL_ADDRESS.id,
          MENUS.CLIENT.SSN.id,
          MENUS.CLIENT.DOB.id,
          MENUS.CLIENT.ALTERNATE_PHONE.id,
          MENUS.CLIENT.EMPLOYMENT_STATUS.id,
          MENUS.CLIENT.EMPLOYMENT_STATUS_2.id,
          MENUS.CLIENT.BUSINESS.id,
          MENUS.CLIENT.ANNUAL_INCOME.id,
          MENUS.CLIENT.WEALTH_SOURCE.id,
          MENUS.CLIENT.POLITICALLY_EXPOSED_PERSON.id,
          MENUS.CLIENT.COMPANY_ADDRESS.id,
          MENUS.CLIENT.PERSHING_COMPANY_ADDRESS.id,
          MENUS.CLIENT.JOB_DESCRIPTION.id,
          MENUS.CLIENT.COMPANY_OFFICIAL.id,
          MENUS.CLIENT.BENEFICIAL_INTEREST.id,
          MENUS.CLIENT.ACCOUNT_PRIMARY_BENEFICIARIES.id,
          MENUS.CLIENT.ACCOUNT_CONTINGENT_BENEFICIARIES.id,
          MENUS.CLIENT.INHERITEDIRA.id,
          MENUS.CLIENT.TRUST_ACCOUNT.id,
          MENUS.CLIENT.ENTITY_ACCOUNT.id,
          MENUS.CLIENT.TRUST_INFO.id,
          MENUS.CLIENT.TRUSTEES.id,
          MENUS.CLIENT.GRANTORS.id,
          MENUS.CLIENT.ENTITY_INFO.id,
          MENUS.CLIENT.AUTHORIZED_SIGNERS.id,
          MENUS.CLIENT.CONSOLIDATED_STATEMENT.id,
          MENUS.CLIENT.PERSHING_CONSOLIDATED_STATEMENT.id,
          MENUS.CLIENT.CUSTOM_QUESTIONS.id,
          MENUS.CLIENT.FINISH.id,
          MENUS.CLIENT.HOME.id,
          MENUS.CLIENT.PERSHING_EMPLOYER_RISK.id
        ];
        
        if (clientInterviewPages.includes(route.name)) {
          return; // Don't purge auth for client interview pages
        }
      }
      
      // Original check for path-based routes
      if (routePath && !routePath.includes('client-interview')) {
        this.$store.dispatch(PURGE_AUTHENTICATED_INTERVIEW);
      }
    },

    /* 
      toggle form state from hidden/shown.
      If hidden, reset form errors
    */
    toggleFormState(hide) {
      this.formHidden = hide;
      if (hide) {
        this.$v.$reset();
      }
    },

    /* 
      Go to the next route.
    */
    goNext(route) {
      let routeObj = typeof route === 'object' ? route : { name: route };
      
      // Safety check for custom_questions routes - ensure questionId parameter is provided
      if ((typeof route === 'string' && route === MENUS.CLIENT.CUSTOM_QUESTIONS.id) || 
          (typeof route === 'object' && route.name === MENUS.CLIENT.CUSTOM_QUESTIONS.id)) {
        
        // If no params or missing questionId, try to get the first question route
        if (!routeObj.params || !routeObj.params.questionId) {
          const nextCustomQuestionRoute = this.getNextCustomQuestionRoute();
          if (nextCustomQuestionRoute) {
            routeObj = nextCustomQuestionRoute;
          } else {
            // No custom questions available, redirect to finish
            console.warn('Attempted to navigate to custom_questions without questionId parameter');
            routeObj = { name: MENUS.CLIENT.FINISH.id };
          }
        }
      }
      
      if (this.formErrors) this.formErrors.$reset();
      this.ignoreInProgessClient = true;
      
      // Check if there are any pages syncing before navigation
      this.checkForSyncingPages();
      
      // Fetch updated interview progress before navigation
      this.$store.dispatch(GET_INTERVIEW_PROGRESS).finally(() => {
        this.$router.push(routeObj).catch((err) => {
          console.error('Navigation error:', err);
        });
      });
    },

    /*
      Go to previous route 
    */
    goPrev(route) {
      let routeObj = typeof route === 'object' ? route : { name: route };
      
      // Safety check for custom_questions routes - ensure questionId parameter is provided
      if ((typeof route === 'string' && route === MENUS.CLIENT.CUSTOM_QUESTIONS.id) || 
          (typeof route === 'object' && route.name === MENUS.CLIENT.CUSTOM_QUESTIONS.id)) {
        
        // If no params or missing questionId, try to get the previous question route
        if (!routeObj.params || !routeObj.params.questionId) {
          const prevCustomQuestionRoute = this.getPreviousCustomQuestionRoute();
          if (prevCustomQuestionRoute) {
            routeObj = prevCustomQuestionRoute;
          } else {
            // No custom questions available, redirect to beneficial interest
            console.warn('Attempted to navigate to custom_questions without questionId parameter');
            routeObj = { name: MENUS.CLIENT.BENEFICIAL_INTEREST.id };
          }
        }
      }
      
      if (this.formErrors) this.formErrors.$reset();
      this.ignoreInProgessClient = true;
      
      // Don't fetch interview progress for backward navigation - just navigate
      // This avoids potential authentication issues during navigation
      this.$router.push(routeObj).catch((err) => {
        console.error('Navigation error:', err);
      });
    },

    /*
      Check if there are any pages with 'IN_PROGRESS' status
      and ensure they will be displayed as syncing
    */
    checkForSyncingPages() {
      if (!this.clientProgress || !this.clientProgress.progress) {
        return;
      }
      
      // Find any pages with IN_PROGRESS status
      const syncingPages = this.clientProgress.progress.filter(page => 
        page.status === 'IN_PROGRESS'
      );
      
      // Make sure they're still marked as syncing when we navigate
      if (syncingPages.length > 0) {
        // Force a reactive update to ensure components re-render
        this.$nextTick(() => {
          this.$store.dispatch(GET_INTERVIEW_PROGRESS);
        });
      }
    },

    /*
      Remove a page from the page list (if present)
    */
    removePage(pageName, instance = null) {
      const progressPage = this.getPage(pageName, instance);
      if (progressPage) {
        const pageIndex = this.clientProgress.progress.findIndex((p) => {
          if (instance) {
            return (
              p.pageName === pageName &&
              p.instance === instance.name &&
              p.label === instance.label
            );
          }
          return p.pageName === pageName;
        });
        this.clientProgress.progress.splice(pageIndex, 1);
      }
    },

    /*
      Add a page to the page list (if not present)
    */
    addPage(pageName, instance = null) {
      let progressPage = this.getPage(pageName, instance);
      if (!progressPage) {
        progressPage = {
          id: null,
          interviewName: 'onbord',
          pageName: pageName,
          lastCompleted: null,
          instance: null,
          label: null,
        };
        if (instance) {
          progressPage.instance = instance.name;
          progressPage.label = instance.label;
        }
        this.clientProgress.progress.push(progressPage);
      }
    },

    /*
      Complete a step.
      Save Interview page data to backend.
      Update working (in memory) progress
      TODO: In future - just make API call to get updated progress?
                        Implies no in-memory progress... 
    */
    completeStep(pageName, data, instance = null) {
      const payload = {
        interviewName: 'onbord',
        pageName: pageName,
        instance: null,
        data: data,
      };
      if (instance) {
        payload.instance = instance.name;
        payload.label = instance.label;
      }

      const progressPage = this.getPage(pageName, instance);
      if (progressPage) {
        payload.id = progressPage.id;
        // Immediately mark as in progress to show syncing UI
        progressPage.status = 'IN_PROGRESS';
      }

      // Update backend interview data
      return new Promise((resolve, reject) => {
        this.$store
          .dispatch(UPDATE_INTERVIEW, payload)
          .then(() => {
            // Always refresh progress after updating to ensure we have the latest sync status
            this.$store
              .dispatch(GET_INTERVIEW_PROGRESS)
              .finally(() => resolve());
          })
          .catch((response) => {
            // Mark page as incomplete?
            if (progressPage) {
              progressPage.lastCompleted = null;
              progressPage.status = null; // Clear in-progress status on error
            }

            // Backend returned Error
            NotificationService.alert(
              {
                type: NOTIFICATION_TYPES.ERROR,
                title: 'Error',
                message: 'Error updating progress!',
                okTitle: 'Ok',
                okMethod: () => {
                  this.$store
                    .dispatch(GET_INTERVIEW_PROGRESS)
                    .finally(() => reject());
                },
              },
              response,
            );
          });
      });
    },

    /* 
      Get next Beneficiary Route when skip/continue called
    */
    getNextBeneficiaryRoute(instance) {
      const accounts = this.clientData.accounts;

      // Are we currently on a Primary Beneficiary Screen?
      const onPrimaryScreen =
        this.$route.name === MENUS.CLIENT.ACCOUNT_PRIMARY_BENEFICIARIES.id;

      // Find current account instance
      const index = accounts.findIndex(
        (a) =>
          a.instance.name === instance.name &&
          a.instance.label === instance.label,
      );

      // Found account
      if (index >= 0) {
        const hasContingentBeneficiaryPage =
          this.getPage(
            MENUS.CLIENT.ACCOUNT_CONTINGENT_BENEFICIARIES.id,
            accounts[index].instance,
          ) !== undefined;

        // If we are on the Primary page for account and account has Contingent page available
        // Go to the Contingent page next
        if (onPrimaryScreen && hasContingentBeneficiaryPage) {
          return {
            name: MENUS.CLIENT.ACCOUNT_CONTINGENT_BENEFICIARIES.id,
            params: {
              name: accounts[index].instance.name,
              label: accounts[index].instance.label,
            },
          };
        }

        // Otherwise,
        // If we are not the last account (and are either on a Contingent page or no Contingent Page available for account)
        // Go to next account Primary page.
        if (index + 1 < accounts.length)
          return {
            name: MENUS.CLIENT.ACCOUNT_PRIMARY_BENEFICIARIES.id,
            params: {
              name: accounts[index + 1].instance.name,
              label: accounts[index + 1].instance.label,
            },
          };
      }

      // No next beneficiary page exits
      return null;
    },

    /* 
      Get next Beneficiary Route when previous called
    */
    getPrevBeneficiaryRoute(instance) {
      const accounts = this.clientData.accounts;

      // Are we on a Contingent Beneficiary page?
      const onContingentScreen =
        this.$route.name === MENUS.CLIENT.ACCOUNT_CONTINGENT_BENEFICIARIES.id;

      // Find current account instance
      const index = accounts.findIndex(
        (a) =>
          a.instance.name === instance.name &&
          a.instance.label === instance.label,
      );

      // Found account
      if (index >= 0) {
        // If we are on a Contingent page, go to Primary page for current account
        if (onContingentScreen) {
          return {
            name: MENUS.CLIENT.ACCOUNT_PRIMARY_BENEFICIARIES.id,
            params: {
              name: accounts[index].instance.name,
              label: accounts[index].instance.label,
            },
          };
        }

        // Otherwise (we are on a Primary Page)
        // If first account, no previous beneficiary page available.
        if (index === 0) return null;

        // Not first account
        // If previous account has a Contingent Beneficiary Page, go to that
        const hasContingentBeneficiaryPage =
          this.getPage(
            MENUS.CLIENT.ACCOUNT_CONTINGENT_BENEFICIARIES.id,
            accounts[index - 1].instance,
          ) !== undefined;
        if (hasContingentBeneficiaryPage) {
          return {
            name: MENUS.CLIENT.ACCOUNT_CONTINGENT_BENEFICIARIES.id,
            params: {
              name: accounts[index - 1].instance.name,
              label: accounts[index - 1].instance.label,
            },
          };
        }

        // Otherwise go to previous account primary page
        return {
          name: MENUS.CLIENT.ACCOUNT_PRIMARY_BENEFICIARIES.id,
          params: {
            name: accounts[index - 1].instance.name,
            label: accounts[index - 1].instance.label,
          },
        };
      }

      // No previous beneficiary
      return null;
    },

    getNextCustomQuestionRoute(currentQuestionId = null) {
      const questionPages = this.clientProgress.progress.filter(
        (p) => p.pageName === MENUS.CLIENT.CUSTOM_QUESTIONS.id,
      );

      if (questionPages.length === 0) return null;

      if (!currentQuestionId) {
        const firstQuestion =
          questionPages.length > 0 ? questionPages[0] : null;

        return firstQuestion
          ? {
              name: MENUS.CLIENT.CUSTOM_QUESTIONS.id,
              params: {
                questionId: firstQuestion.data.question._id,
              },
            }
          : null;
      }

      const currentQuestionIndex = currentQuestionId
        ? questionPages.findIndex(
            (p) => p.data.question._id === currentQuestionId,
          )
        : null;

      if (
        currentQuestionIndex !== -1 &&
        currentQuestionIndex + 1 < questionPages.length
      ) {
        return {
          name: MENUS.CLIENT.CUSTOM_QUESTIONS.id,
          params: {
            questionId:
              questionPages[currentQuestionIndex + 1].data.question._id,
          },
        };
      }

      return null;
    },

    getPreviousCustomQuestionRoute(currentQuestionId = null) {
      const questionPages = this.clientProgress.progress.filter(
        (p) => p.pageName === MENUS.CLIENT.CUSTOM_QUESTIONS.id,
      );
      if (!currentQuestionId) {
        const lastQuestion =
          questionPages.length > 0
            ? questionPages[questionPages.length - 1]
            : null;

        return lastQuestion
          ? {
              name: MENUS.CLIENT.CUSTOM_QUESTIONS.id,
              params: {
                questionId: lastQuestion.data.question._id,
              },
            }
          : null;
      }

      const currentQuestionIndex = currentQuestionId
        ? questionPages.findIndex(
            (p) => p.data.question._id === currentQuestionId,
          )
        : null;

      if (currentQuestionIndex !== -1 && currentQuestionIndex > 0) {
        return {
          name: MENUS.CLIENT.CUSTOM_QUESTIONS.id,
          params: {
            questionId:
              questionPages[currentQuestionIndex - 1].data.question._id,
          },
        };
      }

      return null;
    },

    getCustomQuestion(questionId) {
      const questionPages = this.clientProgress.progress.filter(
        (p) => p.pageName === MENUS.CLIENT.CUSTOM_QUESTIONS.id,
      );
      const questionPage = questionPages.find(
        (p) => p.data.question._id === questionId,
      );
      return questionPage ? questionPage.data.question : null;
    },

    getCustomQuestionPageIndex(questionId) {
      const questionPages = this.clientProgress.progress.filter(
        (p) => p.pageName === MENUS.CLIENT.CUSTOM_QUESTIONS.id,
      );
      return questionPages.findIndex((p) => p.data.question._id === questionId);
    },

    /* 
      Check if a specific page is syncing (has IN_PROGRESS status)
    */
    isPageSyncing(pageName, instance = null) {
      if (!this.clientProgress || !this.clientProgress.progress) {
        return false;
      }
      
      const page = this.getPage(pageName, instance);
      return page && page.status === 'IN_PROGRESS';
    },

    /*
      Mark a specific page as syncing (IN_PROGRESS status)
      This allows immediate UI feedback before API response
      
      @param {String} pageName - The name/ID of the page
      @param {Object} instance - Optional instance object for pages with multiple instances
                                For custom questions, should include questionId property
    */
    markPageAsSyncing(pageName, instance = null) {
      if (!this.clientProgress || !this.clientProgress.progress) {
        console.warn('Cannot mark page as syncing: clientProgress or progress array is missing');
        return;
      }
      
      // First try to find the page using regular getPage method
      let page = this.getPage(pageName, instance);
      
      // If page not found and this is a custom question, try direct search
      if (!page && instance && instance.questionId) {
        page = this.findCustomQuestionPage(pageName, instance.questionId);
        
        if (page) {
          console.info(`Found custom question page by direct search (${instance.questionId})`);
        }
      }
      
      if (page) {
        // Mark the page as syncing
        page.status = 'IN_PROGRESS';
        
        if (process.env.NODE_ENV !== 'production') {
          console.log(`Page marked as syncing: ${pageName}`, {
            questionId: instance?.questionId,
            status: page.status
          });
        }
      } else {
        console.warn(`Could not find page to mark as syncing: ${pageName}`, instance);
      }
    },
    
    /*
      Helper to find a custom question page directly
      @param {String} pageName - The name/ID of the page (usually MENUS.CLIENT.CUSTOM_QUESTIONS.id)
      @param {String} questionId - The ID of the custom question
      @returns {Object|null} The page object if found, null otherwise
    */
    findCustomQuestionPage(pageName, questionId) {
      if (!this.clientProgress || !this.clientProgress.progress) {
        return null;
      }
      
      return this.clientProgress.progress.find(p => 
        p.pageName === pageName && 
        p.data && 
        p.data.question && 
        p.data.question._id === questionId
      );
    },
  },

  created() {
    /* 
      On navigate from menu
      Leave and close nav drawer
    */
    this.listen('navigate-to', (route) => {
      this.ignoreInProgessClient = true;
      const routeObj = typeof route === 'object' ? route : { name: route };
      this.$router.push(routeObj).catch(() => {
        EventService.emit('toggle-main-drawer');
      });
    });

    /* 
      On navigate from menu with params
      Used specifically for routes that require parameters
    */
    this.listen('navigate-to-params', (routeObj) => {
      this.ignoreInProgessClient = true;
      this.$router.push(routeObj).catch(() => {
        EventService.emit('toggle-main-drawer');
      });
    });

    // Prompt leave via manual url change if dirty
    window.addEventListener('beforeunload', function (e) {
      // Cancel the event
      e.preventDefault(); // If you prevent default behavior in Mozilla Firefox prompt will always be shown
      // Chrome requires returnValue to be set
      e.returnValue = '';
    });
  },
};
export default InterviewClientMixin;
